import { AuthConfig } from 'softcat:config/auth.ts';
import { UserPreferencesConfig } from 'softcat:config/user-preferences.ts';

type RequestOptions = Omit<RequestInit, 'body' | 'method'> & {
    authenticated?: boolean;
    body?: RequestInit['body'] | object;
};

type RequestParameters = Omit<RequestInit, 'method'> & {
    method: string;
    options?: RequestOptions;
    url: string;
};

type TokenProvider = () => string | undefined;

export const DEFAULT_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

let baseUrl = DEFAULT_BASE_URL;

let tokenProvider: TokenProvider = () => {
    return localStorage.getItem(AuthConfig.AccessTokenStorageKey) ?? undefined;
};

const setBaseUrl = (url: string) => {
    baseUrl = url;
};

const resetBaseUrl = () => {
    baseUrl = DEFAULT_BASE_URL;
};

const setTokenProvider = (provider: TokenProvider) => {
    tokenProvider = provider;
};

const resetTokenProvider = () => {
    tokenProvider = () => {
        return localStorage.getItem(AuthConfig.AccessTokenStorageKey) ?? undefined;
    };
};

export class ApiError extends Error {
    status: number;

    constructor(message: string, status: number) {
        super(message);
        this.status = status;
    }
}

export type ResponseWithHeaders<T> = { data: T; headers: Headers };

const handleResponse = async <T>(response: Response): Promise<T> => {
    if (response.status === 401) {
        localStorage.removeItem(AuthConfig.AccessTokenStorageKey);
        localStorage.removeItem(UserPreferencesConfig.TerritoryStorageKey);

        globalThis.location.assign(`${import.meta.env.BASE_URL}login`);

        throw new ApiError('Unauthorized. Redirecting to login.', 401);
    }

    if (!response.ok) {
        let message = 'Unknown error';
        try {
            const error = await response.json();
            message = error.message || error.error_description || message;
        } catch {
            message = await response.text().catch(() => {
                return message;
            });
        }

        throw new ApiError(message, response.status);
    }

    const text = await response.text();

    return (text ? JSON.parse(text) : undefined) as T;
};

const requestWithHeaders = async <T>({
    method,
    options,
    url,
}: RequestParameters): Promise<ResponseWithHeaders<T>> => {
    const { authenticated = true, ...requestOptions } = options || {};
    const token = tokenProvider();

    const headers: Record<string, string> = {
        ...(authenticated ? { Authorization: `Bearer ${token}` } : {}),
        ...(requestOptions.headers as Record<string, string>),
    };

    let { body } = requestOptions;

    if (body && !(body instanceof FormData) && typeof body === 'object') {
        headers['Content-Type'] = headers['Content-Type'] || 'application/json';
        body = JSON.stringify(body);
    }

    const response = await fetch(`${baseUrl}${url}`, {
        ...requestOptions,
        body,
        headers,
        method,
    });

    return { data: await handleResponse<T>(response), headers: response.headers };
};

const request = async <T>(parameters: RequestParameters): Promise<T> => {
    const { data } = await requestWithHeaders<T>(parameters);

    return data;
};

const get = <T>(url: string, options?: RequestOptions): Promise<T> => {
    return request<T>({ method: 'GET', options, url });
};

const getWithHeaders = <T>(
    url: string,
    options?: RequestOptions,
): Promise<ResponseWithHeaders<T>> => {
    return requestWithHeaders<T>({ method: 'GET', options, url });
};

const post = <T>(url: string, options?: RequestOptions): Promise<T> => {
    return request<T>({ method: 'POST', options, url });
};

const put = <T>(url: string, options?: RequestOptions): Promise<T> => {
    return request<T>({ method: 'PUT', options, url });
};

const remove = <T>(url: string, options?: RequestOptions): Promise<T> => {
    return request<T>({ method: 'DELETE', options, url });
};

export const SoftcatApiUtils = {
    get,
    getWithHeaders,
    post,
    put,
    remove,
    resetBaseUrl,
    resetTokenProvider,
    setBaseUrl,
    setTokenProvider,
};
